/**
 * Loss Simulator — Main orchestrator
 *
 * Select assets to "lose" (with quantity), run cascade simulation, view time-phased degradation.
 */

import { useState, useEffect, useCallback } from 'react'
import type { AssetSummary, AssetDetail, FusionCluster } from '../../../../types'
import { getForceComposition, getAssetDetail, getFusionClusters, predict } from '../../../../services/api'
import {
  buildDependencyGraph,
  applyDegradationToVector,
  getDegradationStatus,
  TIME_POINTS,
  type CascadeEdge,
  type AssetCascadeResult,
  type CascadeResult,
  type TimePoint,
} from '../../../../services/cascadeEngine'
import { AssetLossSelector } from './AssetLossSelector'
import { TimeSlider } from './TimeSlider'
import { CascadeSummaryBar } from './CascadeSummaryBar'
import { ImpactGrid } from './ImpactGrid'

// Build feature vector (same logic as TacticalPicturePanel)
function buildFeatureVector(detail: AssetDetail): number[] {
  const om = detail.operational_metrics
  const fp = detail.fingerprint
  const count = detail.assigned_count
  const assets_available = om.assets_available
  const assets_in_maintenance = om.assets_in_maintenance
  const total_force = count
  const sorties = om.sorties_per_period
  const mission_eff = om.mission_effectiveness
  const sorties_generated = sorties
  const missions_effective = sorties * mission_eff
  const isr_hours_flown = (fp.mission / 100) * assets_available * 2.5
  const turnaround_hrs = om.turnaround_min / 60
  const flight_hours_consumed = sorties * turnaround_hrs
  const committed_taskings = Math.round(sorties * (fp.readiness / 100) * 0.8)
  const mission_surplus = missions_effective - committed_taskings
  const objectives_achieved = detail.readiness_score * mission_eff
  const munitions_on_hand = assets_available * (fp.logistics / 100) * 15
  const uncommitted_capacity = Math.max(0, assets_available - committed_taskings * 0.3)
  const scheduled_missions_24h = Math.round(sorties * 0.6)
  const scheduled_missions_72h = Math.round(sorties * 1.6)
  const mission_effectiveness_rate = mission_eff
  const net_available_capacity = Math.max(0, assets_available - committed_taskings * 0.25)
  const capacity_utilization_ratio = assets_available > 0 ? Math.min(1.0, committed_taskings / assets_available) : 0
  const tasking_load_ratio = total_force > 0 ? Math.min(1.0, committed_taskings / total_force) : 0
  const munitions_depth_ratio = munitions_on_hand / (committed_taskings * 3 + 1)
  const schedule_density_ratio = scheduled_missions_24h / (assets_available + 1)

  return [
    sorties_generated, missions_effective, isr_hours_flown, flight_hours_consumed,
    mission_surplus, objectives_achieved, assets_available, assets_in_maintenance,
    munitions_on_hand, total_force, committed_taskings, uncommitted_capacity,
    scheduled_missions_24h, scheduled_missions_72h, mission_effectiveness_rate,
    net_available_capacity, capacity_utilization_ratio, tasking_load_ratio,
    munitions_depth_ratio, schedule_density_ratio,
  ].map(v => Math.round(v * 10000) / 10000)
}

export function LossSimulatorView() {
  const [assets, setAssets] = useState<AssetSummary[]>([])
  const [assetDetails, setAssetDetails] = useState<Record<string, AssetDetail>>({})
  const [clusters, setClusters] = useState<FusionCluster[]>([])
  const [loading, setLoading] = useState(true)

  // Simulation state: asset ID → count lost
  const [lostAssets, setLostAssets] = useState<Record<string, number>>({})
  const [currentTime, setCurrentTime] = useState<TimePoint>(0)
  const [cascadeResult, setCascadeResult] = useState<CascadeResult | null>(null)
  const [isSimulating, setIsSimulating] = useState(false)
  const [isAnimating, setIsAnimating] = useState(false)

  // Load data
  const fetchData = useCallback(async () => {
    setLoading(true)
    const [forceRes, clusterRes] = await Promise.all([getForceComposition(), getFusionClusters()])

    if (forceRes.data) {
      setAssets(forceRes.data.assets)
      const detailPromises = forceRes.data.assets.map(a =>
        getAssetDetail(a.id).then(res => ({ id: a.id, detail: res.data }))
      )
      const details = await Promise.all(detailPromises)
      const map: Record<string, AssetDetail> = {}
      for (const { id, detail } of details) {
        if (detail) map[id] = detail
      }
      setAssetDetails(map)
    }

    if (clusterRes.data) setClusters(clusterRes.data)
    setLoading(false)
  }, [])

  useEffect(() => { fetchData() }, [fetchData])

  // Update lost count for an asset
  const updateLostCount = useCallback((assetId: string, delta: number) => {
    setLostAssets(prev => {
      const asset = assets.find(a => a.id === assetId)
      if (!asset) return prev

      const currentCount = prev[assetId] ?? 0
      const newCount = Math.max(0, Math.min(asset.assigned_count, currentCount + delta))

      // Remove key when 0
      if (newCount === 0) {
        const next = { ...prev }
        delete next[assetId]
        return next
      }

      // Enforce 5 asset-type max
      if (!(assetId in prev) && Object.keys(prev).length >= 5) return prev

      return { ...prev, [assetId]: newCount }
    })
    setCascadeResult(null) // Reset results when selection changes
  }, [assets])

  // Run simulation
  const runSimulation = useCallback(async () => {
    const lostIds = Object.keys(lostAssets)
    if (lostIds.length === 0) return

    setIsSimulating(true)
    setCascadeResult(null)

    // Map lost asset IDs to asset type names and compute loss ratios
    const lostTypes: string[] = []
    const lossRatioMap: Record<string, number> = {}

    for (const id of lostIds) {
      const detail = assetDetails[id]
      if (!detail) continue
      const assetType = detail.asset_type
      lostTypes.push(assetType)
      lossRatioMap[assetType] = lostAssets[id] / detail.assigned_count
    }

    // Build dependency graph with loss ratios for partial attrition
    const edges = buildDependencyGraph(clusters, lostTypes, assetDetails, lossRatioMap)

    // Group edges by target asset
    const edgesByTarget: Record<string, CascadeEdge[]> = {}
    for (const edge of edges) {
      const targetId = Object.entries(assetDetails).find(([, d]) => d.asset_type === edge.toAsset)?.[0]
      if (targetId) {
        if (!edgesByTarget[targetId]) edgesByTarget[targetId] = []
        edgesByTarget[targetId].push(edge)
      }
    }

    // Run baseline + degraded predictions for all impacted assets at all time points
    const results: AssetCascadeResult[] = []

    for (const asset of assets) {
      const detail = assetDetails[asset.id]
      if (!detail) continue

      const lostCount = lostAssets[asset.id] ?? 0
      const isFullyLost = lostCount >= asset.assigned_count
      const isPartiallyLost = lostCount > 0 && !isFullyLost
      const lossRatio = lostCount / asset.assigned_count
      const assetEdges = edgesByTarget[asset.id] ?? []

      if (isFullyLost) {
        // All units lost — full denial
        results.push({
          assetId: asset.id,
          assetType: asset.asset_type,
          domain: asset.domain,
          role: detail.role,
          isLost: true,
          baselineCapacity: 0,
          capacityByTime: { 0: 0, 15: 0, 30: 0, 60: 0, 120: 0 },
          degradationByTime: { 0: 1, 15: 1, 30: 1, 60: 1, 120: 1 },
          status: 'MISSION_DENIED',
          impactChains: [],
        })
        continue
      }

      // Get baseline prediction
      const baseFeatures = buildFeatureVector(detail)
      const baseRes = await predict(asset.asset_type, baseFeatures)
      const baselineCapacity = baseRes.data?.predicted_capacity ?? 0

      if (isPartiallyLost) {
        // Partially lost — capacity scales proportionally, plus cascade effects
        const remainingRatio = 1 - lossRatio
        const capacityByTime: Record<number, number> = {}
        const degradationByTime: Record<number, number> = {}

        if (assetEdges.length > 0) {
          // Has cascade effects too
          for (const tp of TIME_POINTS) {
            const degradedFeatures = applyDegradationToVector(baseFeatures, assetEdges, tp)
            const degRes = await predict(asset.asset_type, degradedFeatures)
            const cascadeCap = degRes.data?.predicted_capacity ?? baselineCapacity
            const cap = cascadeCap * remainingRatio
            capacityByTime[tp] = cap
            degradationByTime[tp] = baselineCapacity > 0 ? 1 - (cap / baselineCapacity) : 0
          }
        } else {
          for (const tp of TIME_POINTS) {
            const cap = baselineCapacity * remainingRatio
            capacityByTime[tp] = cap
            degradationByTime[tp] = lossRatio
          }
        }

        const status = lossRatio >= 1.0 ? 'MISSION_DENIED' as const
          : lossRatio >= 0.5 ? 'SEVERELY_DEGRADED' as const
          : 'DEGRADED' as const

        results.push({
          assetId: asset.id,
          assetType: asset.asset_type,
          domain: asset.domain,
          role: detail.role,
          isLost: false,
          baselineCapacity,
          capacityByTime: capacityByTime as Record<TimePoint, number>,
          degradationByTime: degradationByTime as Record<TimePoint, number>,
          status,
          impactChains: assetEdges,
        })
        continue
      }

      if (assetEdges.length === 0) {
        // Not impacted at all
        results.push({
          assetId: asset.id,
          assetType: asset.asset_type,
          domain: asset.domain,
          role: detail.role,
          isLost: false,
          baselineCapacity,
          capacityByTime: { 0: baselineCapacity, 15: baselineCapacity, 30: baselineCapacity, 60: baselineCapacity, 120: baselineCapacity },
          degradationByTime: { 0: 0, 15: 0, 30: 0, 60: 0, 120: 0 },
          status: 'NOMINAL',
          impactChains: assetEdges,
        })
        continue
      }

      // Cascade-only impact (not directly lost)
      const capacityByTime: Record<number, number> = {}
      const degradationByTime: Record<number, number> = {}

      for (const tp of TIME_POINTS) {
        const degradedFeatures = applyDegradationToVector(baseFeatures, assetEdges, tp)
        const degRes = await predict(asset.asset_type, degradedFeatures)
        const cap = degRes.data?.predicted_capacity ?? baselineCapacity
        capacityByTime[tp] = cap
        degradationByTime[tp] = baselineCapacity > 0 ? 1 - (cap / baselineCapacity) : 0
      }

      const finalCap = capacityByTime[120] ?? baselineCapacity
      const status = getDegradationStatus(baselineCapacity, finalCap)

      results.push({
        assetId: asset.id,
        assetType: asset.asset_type,
        domain: asset.domain,
        role: detail.role,
        isLost: false,
        baselineCapacity,
        capacityByTime: capacityByTime as Record<TimePoint, number>,
        degradationByTime: degradationByTime as Record<TimePoint, number>,
        status,
        impactChains: assetEdges,
      })
    }

    // Compute summary
    const lostIds2 = Object.keys(lostAssets)
    const totalUnitsLost = Object.values(lostAssets).reduce((s, n) => s + n, 0)
    const impacted = results.filter(r => !r.isLost && r.impactChains.length > 0 && !(r.assetId in lostAssets))
    const avgDelta = impacted.length > 0
      ? impacted.reduce((s, r) => s + (r.capacityByTime[120] - r.baselineCapacity), 0) / impacted.length
      : 0
    const escalations = results.filter(r => r.status === 'SEVERELY_DEGRADED' || r.status === 'MISSION_DENIED').length
    const denied = results.filter(r => r.status === 'MISSION_DENIED').length

    setCascadeResult({
      lostAssets,
      assets: results,
      summary: {
        assetsLost: lostIds2.length,
        totalUnitsLost,
        assetsImpacted: impacted.length,
        avgCapacityDelta: Math.round(avgDelta * 10) / 10,
        threatEscalations: escalations,
        missionsDenied: denied,
      },
    })
    setCurrentTime(0)
    setIsSimulating(false)
  }, [lostAssets, assets, assetDetails, clusters])

  // Animation
  useEffect(() => {
    if (!isAnimating || !cascadeResult) return
    let idx = 0
    const interval = setInterval(() => {
      idx++
      if (idx >= TIME_POINTS.length) {
        setIsAnimating(false)
        clearInterval(interval)
        return
      }
      setCurrentTime(TIME_POINTS[idx])
    }, 1600)
    return () => clearInterval(interval)
  }, [isAnimating, cascadeResult])

  const startAnimation = useCallback(() => {
    setCurrentTime(0)
    setIsAnimating(true)
  }, [])

  if (loading) return <div className="animate-pulse h-full bg-term-bgLight rounded" />

  return (
    <div className="flex flex-col gap-4 h-full overflow-auto">
      {/* Asset Loss Selector */}
      <AssetLossSelector
        assets={assets}
        assetDetails={assetDetails}
        lostAssets={lostAssets}
        onUpdateCount={updateLostCount}
        onRun={runSimulation}
        isSimulating={isSimulating}
      />

      {/* Time Slider + Animate */}
      {cascadeResult && (
        <>
          <TimeSlider
            currentTime={currentTime}
            onTimeChange={setCurrentTime}
            onAnimate={startAnimation}
            isAnimating={isAnimating}
          />

          {/* Summary Bar */}
          <CascadeSummaryBar summary={cascadeResult.summary} />

          {/* Impact Grid */}
          <ImpactGrid
            results={cascadeResult.assets}
            currentTime={currentTime}
          />
        </>
      )}

      {!cascadeResult && !isSimulating && Object.keys(lostAssets).length === 0 && (
        <div className="flex flex-col items-center justify-center flex-1 gap-3">
          <div className="text-3xl opacity-20">⚠</div>
          <div className="text-sm text-term-textDim">Select up to 5 asset types to simulate loss scenarios</div>
          <div className="text-[10px] text-term-textDim">
            Use +/- to set how many of each type are lost for realistic partial-attrition scenarios
          </div>
        </div>
      )}
    </div>
  )
}
